import React, { useMemo, useState } from 'react';
import { X } from 'lucide-react';

const TABS = [
    { key: 'characters', label: 'Characters' },
    { key: 'costumes', label: 'Costumes & Accessories' },
    { key: 'elements', label: 'Props & Elements' },
];

// Flatten project Cast & World into pickable reference images.
export const collectProjectReferenceAssets = (characters) => {
    const list = Array.isArray(characters) ? characters.filter(Boolean) : [];
    const people = list.filter(c => c.category !== 'element');
    const seenCostume = new Set();
    return {
        characters: people.filter(c => c.image).map(c => ({
            url: c.image, kind: 'character', label: c.name || 'Character',
            characterId: c.id, characterName: c.name || 'Character',
            byteplusAssetGroupId: c.byteplusAssetGroupId || null, byteplusAssetId: c.byteplusAssetId || null,
        })),
        costumes: people.flatMap(c => [...(c.costumes || []), ...(c.costumeAndAccessoryImages || [])]
            .filter(u => u && !seenCostume.has(u) && seenCostume.add(u))
            .map((u, i) => ({ url: u, kind: 'costume', label: `${c.name || 'Character'} — costume ${i + 1}`, characterId: c.id, characterName: c.name || 'Character' }))),
        elements: list.filter(c => c.category === 'element' && c.image).map(c => ({
            url: c.image, kind: 'element', label: c.name || 'Element', elementId: c.id,
        })),
    };
};

export const ProjectAssetsPickerModal = ({ characters, onSelect, onClose }) => {
    const groups = useMemo(() => collectProjectReferenceAssets(characters), [characters]);
    const [tab, setTab] = useState('characters');
    const items = groups[tab];

    return (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4" onClick={onClose} data-testid="project-assets-modal">
            <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-3xl max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700">
                    <h3 className="text-base font-semibold text-white">Project assets</h3>
                    <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-700" data-testid="project-assets-close"><X className="w-5 h-5" /></button>
                </div>
                <div className="flex gap-1 px-5 pt-3">
                    {TABS.map(t => (
                        <button key={t.key} onClick={() => setTab(t.key)}
                            className={`px-3 py-1.5 text-xs rounded-md transition-colors ${tab === t.key ? 'bg-purple-600 text-white' : 'bg-gray-800 text-gray-400 hover:text-white'}`}
                            data-testid={`project-assets-tab-${t.key}`}>{t.label} ({groups[t.key].length})</button>
                    ))}
                </div>
                <div className="flex-1 overflow-y-auto p-5">
                    {items.length === 0 ? (
                        <p className="text-sm text-gray-500 text-center py-10" data-testid="project-assets-empty">No images in this category yet.</p>
                    ) : (
                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                            {items.map((it, i) => (
                                <button key={`${it.url}-${i}`} onClick={() => onSelect(it)}
                                    className="group relative aspect-square rounded-lg overflow-hidden border border-gray-700 hover:border-purple-500 transition-colors"
                                    data-testid={`project-asset-${tab}-${i}`}>
                                    <img src={it.url} alt={it.label} className="w-full h-full object-cover" crossOrigin="anonymous" referrerPolicy="no-referrer" />
                                    <span className="absolute bottom-0 inset-x-0 bg-black/70 text-[10px] text-white px-1.5 py-1 truncate">{it.label}</span>
                                    {it.kind === 'character' && it.byteplusAssetId && (
                                        <span className="absolute top-1 left-1 text-[9px] px-1.5 py-0.5 rounded bg-emerald-600 text-white">Seedance asset</span>
                                    )}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
